import React, { useCallback, useState } from 'react';
import Button from 'src/@vymo/ui/atoms/button';
import { Option, RadioGroup } from 'src/@vymo/ui/atoms/radio';
import Text from 'src/@vymo/ui/atoms/text';
import { Body, Footer, Header, Modal, TreeList } from 'src/@vymo/ui/blocks';
import Table from 'src/@vymo/ui/blocks/table';
import { Keys, locale } from 'src/i18n';
import { useFormContext } from '../../formProvider';
import { radioOptions } from './constants';
import { getColumns, getHierarchyOptionsForTreeList } from './queries';
import styles from './index.module.scss';

function HierarchyModal({
  hint,
  source,
  multi,
  value,
  selectedEntities,
  setSelectedEntities,
  setShowModal,
  onChange,
  hierarchyList,
}) {
  const [optionSelected, setOptionSelected] = useState('nameCode');
  const { getUserHierarchy, getHierarchy, getUsers } = useFormContext(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const totalRowCount = (hierarchyList || [])?.length;
  const userHierarchy = getUserHierarchy() ?? {};
  const users = getUsers() ?? {};

  const hierarchy = getHierarchy() ?? {};
  const optionsListForTree = getHierarchyOptionsForTreeList(
    source,
    userHierarchy,
    hierarchy,
  );
  const columns = getColumns(source, users, hierarchy);

  const getTableSelections = () => {
    const selectionMap: Record<string, boolean> = {};
    selectedEntities?.forEach((entity) => {
      selectionMap[entity?.code] = true;
    });

    return selectionMap;
  };

  const onEntitySelection = useCallback(
    (__, selectedRows) => {
      const entityCodes = Object.keys(selectedRows).filter(
        (key) => selectedRows[key],
      );
      const entityItems = hierarchyList
        .filter((entity) => entityCodes?.includes(entity?.id))
        .map((entity) => ({
          code: entity?.id,
          name: entity?.name,
        }));

      setSelectedEntities(entityItems);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(selectedEntities), hierarchyList],
  );

  const onSubmit = () => {
    onChange(
      {
        ...(value ?? {}),
        values: JSON.stringify(selectedEntities),
      },
      {} as React.ChangeEvent<any>,
    );
    setShowModal(false);
  };

  return (
    <Modal
      classNames={styles.entityWithHierarchySelection}
      onClose={() => {
        setShowModal(false);
      }}
    >
      <Header>
        <Text bold type="h4">
          {`Select ${hint}`}
        </Text>
      </Header>
      <Body className={styles.entityWithHierarchySelection__body}>
        <RadioGroup
          type="radio"
          value={optionSelected}
          onChange={(selectedValue) => {
            setOptionSelected(selectedValue?.[0]?.code);
          }}
        >
          {radioOptions?.map((option) => (
            <Option label={option?.label} value={option?.value} />
          ))}
        </RadioGroup>
        {optionSelected === 'nameCode' ? (
          <Table
            classNames={styles.entityWithHierarchySelection__table}
            isAccordion={false}
            tableData={hierarchyList}
            columnConfigs={columns ?? []}
            tableConfig={{
              showSearch: true,
              isRowSelectionEnabled: true,
              singleRowSelection: !multi,
              name: '',
              pagination: {
                visible: true,
                pageSize,
                totalPages: Math.max(1, Math.ceil(totalRowCount / pageSize)),
                currentPage,
                totalItems: totalRowCount,
              },
              selections: getTableSelections(),
              onRowClick: () => {},
            }}
            onRowSelect={onEntitySelection}
            onChangePageSize={(updatedPageSize) => {
              setPageSize(updatedPageSize);
              setCurrentPage(1);
            }}
            onChangePageIndex={(pageIndex) => {
              setCurrentPage(pageIndex + 1);
            }}
          />
        ) : (
          <div className={styles.entityWithHierarchySelection__tree}>
            <TreeList
              value={selectedEntities}
              options={optionsListForTree}
              onChange={(selectedItems) => {
                setSelectedEntities(selectedItems);
              }}
              showChips
              onChipClose={(item) => {
                const updatedSelectedEntities = selectedEntities?.filter(
                  (entity) => entity?.code !== item?.code,
                );
                setSelectedEntities(updatedSelectedEntities);
              }}
            />
          </div>
        )}
      </Body>
      <Footer>
        <Button onClick={onSubmit} disabled={!selectedEntities?.length}>
          {locale(Keys.SUBMIT)}
        </Button>
      </Footer>
    </Modal>
  );
}

export default HierarchyModal;
